export type DrcAircraftState = 'IN_DOCK' | 'FLYING' | 'OUTSIDE_DOCK' | 'UNKNOWN'

export interface DrcLeaseStatus {
  has_drc_lease: boolean
  operator_username?: string | null
  lease_until?: number | null
  lease_version?: number | null
  aircraft_state?: DrcAircraftState | null
  can_takeover?: boolean
  block_reason?: string | null
}

export interface DrcControlSession {
  clientId: string
  dockSn: string
  fencingToken?: number | null
  leaseUntil?: number | null
}

export interface DrcLeaseTakenOverEvent {
  dock_sn?: string | null
  displaced_lease_version?: number | null
  operator_username?: string | null
}

let activeControlSession: DrcControlSession | null = null

export function setDrcControlSession (session: DrcControlSession) {
  activeControlSession = { ...session }
}

export function getDrcControlSession (dockSn?: string): DrcControlSession | null {
  if (!activeControlSession) {
    return null
  }
  if (dockSn && activeControlSession.dockSn !== dockSn) {
    return null
  }
  return { ...activeControlSession }
}

export function isDrcControlSessionActive (
  session: DrcControlSession | null,
  dockSn?: string,
  now = Date.now()
): boolean {
  const leaseUntil = Number(session?.leaseUntil)
  return !!session?.clientId &&
    (!dockSn || session.dockSn === dockSn) &&
    session.fencingToken !== null &&
    session.fencingToken !== undefined &&
    Number.isFinite(leaseUntil) &&
    leaseUntil > now
}

export function clearDrcControlSession (session?: Pick<DrcControlSession, 'clientId' | 'dockSn'>) {
  if (!activeControlSession) {
    return
  }
  if (session && (session.clientId !== activeControlSession.clientId || session.dockSn !== activeControlSession.dockSn)) {
    return
  }
  activeControlSession = null
}

export function getDrcControlHeaders (dockSn: string): Record<string, string> {
  const session = getDrcControlSession(dockSn)
  if (!session?.clientId || session.fencingToken === null || session.fencingToken === undefined) {
    return {}
  }
  return {
    'X-DRC-Client-Id': session.clientId,
    'X-DRC-Fencing-Token': String(session.fencingToken),
  }
}

export function drcConflictReason (error: any): string {
  return String(error?.response?.data?.data?.reason || '')
}

export function drcConflictLease (error: any): DrcLeaseStatus | null {
  const lease = error?.response?.data?.data?.lease
  return lease && typeof lease.has_drc_lease === 'boolean' ? lease as DrcLeaseStatus : null
}

export function drcErrorMessage (error: any, fallback: string): string {
  return String(error?.response?.data?.message || error?.message || fallback)
}

export function drcAircraftStateText (state?: DrcAircraftState | null): string {
  return {
    IN_DOCK: '已回舱',
    FLYING: '飞行中',
    OUTSIDE_DOCK: '已落地但未回舱',
    UNKNOWN: '状态未知',
  }[state || 'UNKNOWN']
}

export function formatDrcLeaseRemaining (leaseUntil: unknown, now = Date.now()): string {
  const deadline = Number(leaseUntil)
  if (!Number.isFinite(deadline)) {
    return '--:--'
  }
  const seconds = Math.max(0, Math.ceil((deadline - now) / 1000))
  const minutes = Math.floor(seconds / 60)
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`
}

export function shouldInvalidateDrcSession (
  event: DrcLeaseTakenOverEvent,
  session: DrcControlSession | null
): boolean {
  const displacedVersion = Number(event.displaced_lease_version)
  const sessionVersion = Number(session?.fencingToken)
  return !!session &&
    String(event.dock_sn || '') === session.dockSn &&
    Number.isFinite(displacedVersion) &&
    Number.isFinite(sessionVersion) &&
    displacedVersion === sessionVersion
}

/** Renew the DRC lease once fewer than this many milliseconds remain (leases are 3600 s). */
export const DRC_LEASE_RENEW_LEAD_MS = 10 * 60 * 1000
/** Fetch a fresh MQTT credential this long before the JWT `expire_time`. */
export const DRC_CREDENTIAL_REFRESH_LEAD_MS = 5 * 60 * 1000
/** Heartbeat silence after which the link counts as lost when the dock also reports DRC disconnected. */
export const DRC_LINK_LOST_AFTER_MS = 15_000
/** Heartbeat silence after which the link counts as lost even without a dock signal. */
export const DRC_LINK_LOST_WITHOUT_DOCK_SIGNAL_MS = 30_000

export interface DrcLeaseGrant {
  fencing_token?: unknown
  lease_until?: unknown
}

/** Number(null) is 0, so blanks must be rejected before the finite check. */
function finiteOrNull (value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : null
}

/**
 * Applies the fence / expiry returned by `drc/enter` to the local session. Every
 * successful enter or renewal bumps the fence server-side, so the stale value
 * must never survive a grant; a grant without a usable field keeps the old one.
 */
export function applyDrcLeaseGrant (session: DrcControlSession, grant: DrcLeaseGrant | null | undefined): DrcControlSession {
  const fencingToken = finiteOrNull(grant?.fencing_token)
  const leaseUntil = finiteOrNull(grant?.lease_until)
  return {
    ...session,
    fencingToken: fencingToken !== null ? fencingToken : session.fencingToken ?? null,
    leaseUntil: leaseUntil !== null ? leaseUntil : session.leaseUntil ?? null,
  }
}

/** True once the lease is inside its renewal window (or already expired). */
export function shouldRenewDrcLease (
  session: DrcControlSession | null,
  now = Date.now(),
  leadMs = DRC_LEASE_RENEW_LEAD_MS
): boolean {
  const leaseUntil = finiteOrNull(session?.leaseUntil)
  return !!session?.clientId && leaseUntil !== null && leaseUntil - now <= leadMs
}

/**
 * Milliseconds until the MQTT credential should be refreshed. `expire_time` is
 * epoch seconds from the backend; epoch milliseconds are tolerated. Returns
 * null when the value is unusable, and never less than one second so a token
 * already inside the lead window is refreshed immediately rather than never.
 */
export function drcCredentialRefreshDelayMs (
  expireTime: unknown,
  now = Date.now(),
  leadMs = DRC_CREDENTIAL_REFRESH_LEAD_MS
): number | null {
  const raw = Number(expireTime)
  if (!Number.isFinite(raw) || raw <= 0) {
    return null
  }
  const expireAtMs = raw > 1e12 ? raw : raw * 1000
  return Math.max(1000, expireAtMs - leadMs - now)
}

export interface DrcLinkHealthInput {
  flightControlActive: boolean
  hasTopics: boolean
  lastHeartbeatAt: number
  now: number
  /** Dock `link_osd.drc_state === CONNECTED`; null when the dock OSD has not said. */
  dockDrcConnected: boolean | null
}

/**
 * Decides when a DRC link that was once healthy is gone, as opposed to a short
 * heartbeat hiccup. The dock leaving command-flight mode confirms the loss
 * quickly; without that signal a longer silence is required, because dock OSD
 * lags a few seconds behind reality.
 */
export function isDrcLinkLost (input: DrcLinkHealthInput): boolean {
  if (!input.flightControlActive || !input.hasTopics || !(input.lastHeartbeatAt > 0)) {
    return false
  }
  const silence = input.now - input.lastHeartbeatAt
  if (silence < DRC_LINK_LOST_AFTER_MS) {
    return false
  }
  return input.dockDrcConnected === false || silence >= DRC_LINK_LOST_WITHOUT_DOCK_SIGNAL_MS
}

export interface DrcExitResult {
  released?: boolean
  device_exit_confirmed?: boolean
  aircraft_state?: string | null
  warning?: string | null
}

/** Operator-facing note when the lease was released but the dock did not confirm leaving DRC. */
export function drcExitWarning (result: DrcExitResult | null | undefined): string | null {
  if (!result) {
    return null
  }
  if (result.warning) {
    return String(result.warning)
  }
  if (result.device_exit_confirmed === false) {
    return '控制权已释放，但机场未确认退出指令飞行控制模式'
  }
  return null
}

export function isDrcConflictError (error: any): boolean {
  return Number(error?.response?.status) === 409
}
